import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  User,
  browserLocalPersistence,
  browserSessionPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { auth, db } from '../firebase';
import { Profile, Role } from '../types';
import { notificationService } from '../services/NotificationService';
import { getOrganization } from '../firebase/organizations';
import { showsQueryKeys, fetchUserShows } from '../hooks/useShowsData';
import { recordWebActivity } from '../firebase/analytics';
import { organizationQueryKeys } from '../hooks/useOrganizationData';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  /** `remember: false` keeps the session to this browser tab, so closing it
   * signs the person out (for shared computers). Defaults to true. */
  signIn: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (requiredRole: Role | Role[]) => boolean;
  /** True only for the super_admin role. hasRole is a hierarchical >= check,
   * so it cannot express "super admin only" - the Admin console gates on
   * this instead. */
  isSuperAdmin: boolean;
  /**
   * May this person create a show? createShow is org_admin and above
   * (show-management.ts), and because hasRole is a >= check,
   * `hasRole(['teacher'])` is true for teachers - which is how every teacher
   * came to see a Create New button the server always refused. Named here so
   * the rule lives in one place rather than being re-derived per call site.
   */
  canCreateShow: boolean;
  refreshProfile: () => Promise<void>;
  /** True from an explicit sign-in until the "Setting the stage" overlay
   * has finished covering the post-login load. Never set on session
   * restore - a returning tab should not get the curtain-up. */
  showStageIntro: boolean;
  dismissStageIntro: () => void;
  /**
   * Why the last sign-out was not the user's own doing, if it wasn't. Set
   * when the server stops recognising the session (account deleted or
   * disabled); the login page reads it once and clears it.
   */
  forcedSignOutReason: ForcedSignOutReason | null;
  clearForcedSignOutReason: () => void;
}

export type ForcedSignOutReason = 'deleted' | 'disabled';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showStageIntro, setShowStageIntro] = useState(false);
  const [forcedSignOutReason, setForcedSignOutReason] = useState<ForcedSignOutReason | null>(null);
  const queryClient = useQueryClient();

  // Firebase keeps an ID token valid for up to an hour after the Auth user
  // is deleted or disabled, so a person removed by an admin stayed signed in
  // with a shell that could load nothing. The profile listener is the signal
  // that arrives within seconds; this ends the session and says why.
  const forceSignOut = async (reason: ForcedSignOutReason) => {
    console.log(`Forced sign-out (${reason})`);
    setForcedSignOutReason(reason);
    try {
      await notificationService.cleanup();
    } catch (error) {
      console.error('Notification cleanup failed during forced sign-out:', error);
    }
    queryClient.clear();
    await signOut(auth);
  };

  // The auth effect below runs once ([] deps), so any `profile` it closes over
  // is captured at first render and stays null forever. Both the
  // "role changed -> invalidate shows" check and the logout cache clear read
  // the previous profile, so they silently never fired. A ref gives those
  // callbacks the current value without re-subscribing the listener.
  // Named to avoid colliding with the Firestore `profileRef` doc handle inside
  // the effect below, which would otherwise shadow it.
  const previousProfileRef = useRef<Profile | null>(null);
  useEffect(() => {
    previousProfileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    let profileUnsubscribe: (() => void) | null = null;

    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', { user: user?.email, uid: user?.uid });
      setUser(user);
      
      // Clean up previous profile listener
      if (profileUnsubscribe) {
        profileUnsubscribe();
        profileUnsubscribe = null;
      }
      
      if (user) {
        // Kick off the shows fetch now rather than when the dashboard mounts.
        // ProtectedRoute holds the whole route behind the profile snapshot, so
        // without this the getUserShows call queues at the end of a serial
        // chain (auth -> profile -> mount -> shows). Prefetching here lets it
        // run in parallel with the profile listener; useShowsData dedupes
        // against the same query key when the dashboard mounts.
        queryClient.prefetchQuery({
          queryKey: showsQueryKeys.userShows(user.uid),
          queryFn: fetchUserShows,
          staleTime: 5 * 60 * 1000,
        });

        // Counts towards the organisation's weekly sign-in totals for its
        // Analytics page. Fire-and-forget; the server ignores super admins
        // and anyone already counted this week.
        recordWebActivity(user.uid);

        // Set up real-time profile listener
        try {
          console.log('Setting up profile listener for user:', user.uid);
          const profileRef = doc(db, 'profiles', user.uid);
          
          profileUnsubscribe = onSnapshot(profileRef, (profileDoc) => {
            console.log('Profile document updated:', profileDoc.exists());
            
            // Only a server snapshot may say "gone" - a cache-only miss just
            // means nothing has been read yet.
            if (!profileDoc.exists() && !profileDoc.metadata.fromCache) {
              void forceSignOut('deleted');
              return;
            }

            if (profileDoc.exists() && profileDoc.data().accountStatus === 'disabled') {
              void forceSignOut('disabled');
              return;
            }

            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              console.log('Updated profile data:', profileData);
              
              // Helper function to safely convert dates
              const safeToDate = (dateValue: any): Date => {
                if (!dateValue) return new Date();
                if (typeof dateValue.toDate === 'function') return dateValue.toDate();
                if (dateValue instanceof Date) return dateValue;
                if (typeof dateValue === 'string') return new Date(dateValue);
                return new Date();
              };

              // Check if emailVerified status changed and invalidate cast data if so
              const newProfile = {
                ...profileData,
                createdAt: safeToDate(profileData.createdAt),
                updatedAt: safeToDate(profileData.updatedAt),
              } as Profile;
              
              // If profile changed significantly (like email verification in show assignments), clear React Query cache
              const prevProfile = previousProfileRef.current;
              if (prevProfile && (
                prevProfile.role !== newProfile.role ||
                prevProfile.organizationId !== newProfile.organizationId ||
                JSON.stringify(prevProfile.showAssignments) !== JSON.stringify(newProfile.showAssignments)
              )) {
                console.log('🔄 Profile significantly changed, invalidating shows cache');
                queryClient.invalidateQueries({ queryKey: showsQueryKeys.all });
              }
              
              setProfile(newProfile);
              
              // Prefetch organization data for instant logo loading
              if (newProfile.organizationId) {
                console.log('🏢 Prefetching organization data for instant loading');
                queryClient.prefetchQuery({
                  queryKey: organizationQueryKeys.byId(newProfile.organizationId),
                  queryFn: () => getOrganization(newProfile.organizationId!),
                  staleTime: 10 * 60 * 1000, // 10 minutes
                });
              }
              
              // Initialize notifications for authenticated users (admin panel pattern)
              if (!notificationService.getNotificationStatus().isInitialized) {
                console.log('🔔 Initializing notifications for authenticated user');
                notificationService.initialize();
              }
            } else {
              console.log('No profile document found for user');
              setProfile(null);
            }
            setLoading(false);
          }, (error) => {
            console.error('Error listening to profile updates:', error);
            setProfile(null);
            setLoading(false);
          });
        } catch (error) {
          console.error('Error setting up profile listener:', error);
          setProfile(null);
          setLoading(false);
        }
      } else {
        // Clear React Query cache when user logs out (only if there was a previous user)
        if (previousProfileRef.current) {
          console.log('🧹 Clearing React Query cache on logout');
          queryClient.clear();
        }
        setProfile(null);
        setLoading(false);
        setShowStageIntro(false);
      }
    });

    return () => {
      authUnsubscribe();
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }
    };
  }, []);

  const signIn = async (email: string, password: string, remember = true) => {
    // Set on every sign-in, not once at startup: persistence belongs to the
    // session being created, and the last person on this browser may have
    // chosen differently. Local is Firebase's web default.
    await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
    await signInWithEmailAndPassword(auth, email, password);
    // Raise the curtain only on an explicit sign-in. The overlay covers the
    // profile fetch, org fetch and dashboard chunk load, and dismisses
    // itself when they settle.
    setShowStageIntro(true);
  };

  const dismissStageIntro = useCallback(() => setShowStageIntro(false), []);

  const logout = async () => {
    console.log('🚪 Logging out user and clearing all cached data');
    // Clean up notifications before signing out
    await notificationService.cleanup();
    // Clear React Query cache before signing out
    queryClient.clear();
    await signOut(auth);
  };

  const refreshProfile = async (): Promise<void> => {
    if (!user) return;
    
    try {
      console.log('Manually refreshing profile for user:', user.uid);
      const profileDoc = await getDoc(doc(db, 'profiles', user.uid));
      
      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        console.log('Refreshed profile data:', profileData);
        // Helper function to safely convert dates
        const safeToDate = (dateValue: any): Date => {
          if (!dateValue) return new Date();
          if (typeof dateValue.toDate === 'function') return dateValue.toDate();
          if (dateValue instanceof Date) return dateValue;
          if (typeof dateValue === 'string') return new Date(dateValue);
          return new Date();
        };

        setProfile({
          ...profileData,
          createdAt: safeToDate(profileData.createdAt),
          updatedAt: safeToDate(profileData.updatedAt),
        } as Profile);
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  };

  const hasRole = (requiredRole: Role | Role[]): boolean => {
    if (!profile) return false;
    
    const roles: Role[] = ['student', 'student_leader', 'teacher', 'org_admin', 'admin', 'super_admin'];
    const userRoleIndex = roles.indexOf(profile.role);
    
    if (Array.isArray(requiredRole)) {
      return requiredRole.some(role => {
        const requiredRoleIndex = roles.indexOf(role);
        return userRoleIndex >= requiredRoleIndex;
      });
    }
    
    const requiredRoleIndex = roles.indexOf(requiredRole);
    return userRoleIndex >= requiredRoleIndex;
  };

  const isSuperAdmin = profile?.role === 'super_admin';
  const canCreateShow = hasRole(['org_admin']);

  const value = {
    user,
    profile,
    loading,
    signIn,
    logout,
    hasRole,
    isSuperAdmin,
    canCreateShow,
    refreshProfile,
    showStageIntro,
    dismissStageIntro,
    forcedSignOutReason,
    clearForcedSignOutReason: () => setForcedSignOutReason(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};